import { apiClient } from '@/services/apiClient';
import type { Contract, Amendment, PaginatedResponse, DocumentVersionHistoryResponse } from '@/types';

export interface ContractListParams {
  page?: number;
  pageSize?: number;
  search?: string;
  vendorId?: number;
  lifecycleStatus?: string;
  departmentScope?: string;
  glCode?: string;
  expirationDateFrom?: string;
  expirationDateTo?: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

export interface CreateContractInput {
  vendorId: number;
  contractTitle: string;
  contractType?: string | null;
  effectiveDate?: string | null;
  expirationDate?: string | null;
  autoRenewalTerms?: string | null;
  cancellationNoticePeriodDays?: number | null;
  contractValue?: number | null;
  paymentTerms?: string | null;
  budgetCategory?: string | null;
  glCode?: string | null;
  departmentScope: string;
  notes?: string | null;
  erpPoNumber?: string | null;
  erpGlCode?: string | null;
  budgetCategories?: string[];
  annualCosts?: Array<{ fiscalYear: number; annualAmount: number; notes?: string | null }>;
  siteNumbers?: string[];
}

export type UpdateContractInput = Partial<CreateContractInput> & { updatedAt: string };

export interface LifecycleTransitionInput {
  targetStatus: string;
  renewalLinkedContractId?: number;
  transitionType?: 'manual' | 'automatic';
}

export interface ContractAnnualCost {
  contractAnnualCostId: number;
  contractId: number;
  fiscalYear: number;
  annualAmount: number;
  notes: string | null;
}

export interface ContractBudgetCategory {
  contractId: number;
  budgetCategory: string;
}

export interface ContractSiteAssociation {
  contractId: number;
  siteNumber: string;
}

export interface LinkedInvoiceSummary {
  invoiceId: number;
  invoiceNumber: string | null;
  invoiceDate: string | null;
  totalAmount: number | null;
  lifecycleStatus: string;
  codingSummary: string | null;
}

export interface ContractSpendVariance {
  contractValue: number;
  totalInvoiced: number;
  variance: number;
  variancePercent: number;
  invoiceCount: number;
  status: 'under' | 'on-track' | 'over';
}

export interface MatchInvoicesResult {
  totalMatched: number;
  contractsProcessed: number;
  results: Array<{
    contractId: number;
    contractTitle: string;
    vendorName: string;
    matched: number;
    alreadyLinked: number;
  }>;
}

export interface ContractDetailResponse {
  contract: Contract;
  amendments: Amendment[];
  annualCosts: ContractAnnualCost[];
  budgetCategories: ContractBudgetCategory[];
  siteAssociations: ContractSiteAssociation[];
  linkedInvoices: LinkedInvoiceSummary[];
  spendVariance: ContractSpendVariance | null;
}

export const contractService = {
  list(params?: ContractListParams): Promise<PaginatedResponse<Contract>> {
    return apiClient.get('contracts', params as Record<string, string | number | boolean | undefined>);
  },

  getById(id: number): Promise<ContractDetailResponse> {
    return apiClient.get(`contracts/${id}`);
  },

  create(input: CreateContractInput): Promise<Contract> {
    return apiClient.post('contracts', input);
  },

  update(id: number, input: UpdateContractInput): Promise<Contract> {
    return apiClient.put(`contracts/${id}`, input);
  },

  delete(id: number): Promise<void> {
    return apiClient.delete(`contracts/${id}`);
  },

  transitionLifecycle(id: number, input: LifecycleTransitionInput): Promise<Contract> {
    return apiClient.put(`contracts/${id}/lifecycle`, input);
  },

  // Amendments
  listAmendments(contractId: number): Promise<Amendment[]> {
    return apiClient.get(`contracts/${contractId}/amendments`);
  },

  createAmendment(contractId: number, input: Omit<Amendment, 'amendmentId' | 'contractId' | 'createdAt' | 'createdBy'>): Promise<Amendment> {
    return apiClient.post(`contracts/${contractId}/amendments`, input);
  },

  // Site associations
  associateSite(contractId: number, siteNumber: string): Promise<void> {
    return apiClient.post(`contracts/${contractId}/sites`, { siteNumber });
  },

  removeSiteAssociation(contractId: number, siteNumber: string): Promise<void> {
    return apiClient.delete(`contracts/${contractId}/sites/${siteNumber}`);
  },

  // Invoice matching
  matchInvoices(contractId?: number): Promise<MatchInvoicesResult> {
    if (contractId) {
      return apiClient.post(`contracts/${contractId}/match-invoices`, {});
    }
    return apiClient.post('contracts/match-invoices', {});
  },

  // Document version history
  getDocumentVersions(contractId: number): Promise<DocumentVersionHistoryResponse> {
    return apiClient.get(`contracts/${contractId}/documents/versions`);
  },

  getDocumentDownloadUrl(contractId: number, versionEntryId?: string): string {
    const base = `/api/contracts/${contractId}/documents/download`;
    if (versionEntryId) {
      return `${base}?versionEntryId=${encodeURIComponent(versionEntryId)}`;
    }
    return base;
  },
};
